import { DISMISS_MODAL, OPEN_CATALOG_ANNOUNCEMENT_MODAL } from './actions';
import api from '@/redux/api/catalogAnnouncement';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import isEmpty from 'lodash/isEmpty';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_CATALOG_ANNOUNCMENT_FAIL = 'LOAD_CATALOG_ANNOUNCMENT_FAIL';
export const LOAD_CATALOG_ANNOUNCMENT_REQUEST = 'LOAD_CATALOG_ANNOUNCMENT_REQUEST';
export const LOAD_CATALOG_ANNOUNCMENT_SUCCESS = 'LOAD_CATALOG_ANNOUNCMENT_SUCCESS';

const REDUX_STORE_TIME = ms('10m');

/* reducer */
const DEFAULT_STATE = {
    activeAnnouncement: 0,
    byId: {},
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case DISMISS_MODAL:
            return {
                ...state,
                activeAnnouncement: 0,
            };
        case LOAD_CATALOG_ANNOUNCMENT_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.catalogId]),
            };
        case LOAD_CATALOG_ANNOUNCMENT_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_CATALOG_ANNOUNCMENT_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;
            existing[action.meta.catalogId] = action.payload;
            loaded[action.meta.catalogId] = time;
            loading = difference(loading, [action.meta.catalogId]);
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        case OPEN_CATALOG_ANNOUNCEMENT_MODAL:
            return {
                ...state,
                activeAnnouncement: action.payload.catalogId,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.catalogAnnouncement;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);
export const getActiveCatalogAnnouncement = createSelector(stateSelector, (state) => state.activeAnnouncement);

export const getCatalogAnnouncement: (state: any, selector: any) => any = createSelector(
    [byIdSelector, idSelector],
    (byId, id) => byId[id] || {}
);

const getLoadTimeForCatalogAnnouncement = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isCatalogAnnouncementLoading = createSelector([loadingSelector, idSelector], (loading, id) =>
    loading.includes(id)
);

const shouldFetchCatalogAnnouncement = createSelector(
    [idSelector, getCatalogAnnouncement, getLoadTimeForCatalogAnnouncement, isCatalogAnnouncementLoading],
    (catalogId, announcement, loaded, loading) => {
        if (!catalogId) {
            return false;
        }
        if (!isEmpty(announcement)) {
            const time = Date.now();
            const diff = time - loaded;
            if (diff < REDUX_STORE_TIME) {
                return false;
            }
        }
        return !loading;
    }
);

const shouldFetchFullCatalogAnnouncement = createSelector(
    [idSelector, getCatalogAnnouncement, getLoadTimeForCatalogAnnouncement, isCatalogAnnouncementLoading],
    (catalogId, announcement, loaded, loading) => {
        if (!catalogId) {
            return false;
        }
        if (!announcement) {
            console.error('ERROR: Must first fetch annoucement for catalog ', catalogId);
            return false;
        }
        if (!isEmpty(announcement)) {
            const time = Date.now();
            const diff = time - loaded;
            if (Boolean(announcement.body)) {
                if (diff < REDUX_STORE_TIME) {
                    return false;
                }
            } else {
                return true;
            }
        }
        return !loading;
    }
);

// /* ACTION CREATORS */
export const fetchCatalogAnnouncementIfNeeded = (catalogId: number, houseId: number) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    if (shouldFetchCatalogAnnouncement(state, catalogId)) {
        const deployment = getDeployment(state);
        try {
            dispatch({
                meta: { actionTime: Date.now() },
                payload: catalogId,
                type: LOAD_CATALOG_ANNOUNCMENT_REQUEST,
            });
            const response = await api.fetchCatalogAnnouncementsByCatalogId({
                catalogId,
                deployment,
                houseId,
            });
            dispatch({
                meta: { actionTime: Date.now(), catalogId },
                payload: response.payload,
                type: LOAD_CATALOG_ANNOUNCMENT_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                meta: { catalogId },
                payload: error,
                type: LOAD_CATALOG_ANNOUNCMENT_FAIL,
            });
        }
    } else {
        return Promise.resolve();
    }
};

export const fetchFullCatalogAnnouncementIfNeeded = (catalogId: number) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    if (shouldFetchFullCatalogAnnouncement(state, catalogId)) {
        const deployment = getDeployment(state);
        const { id } = getCatalogAnnouncement(state, catalogId);
        try {
            dispatch({
                meta: { actionTime: Date.now() },
                payload: catalogId,
                type: LOAD_CATALOG_ANNOUNCMENT_REQUEST,
            });
            const response = await api.fetchCatalogAnnouncementsByAnnouncementId({
                announcementId: id,
                deployment,
            });
            dispatch({
                meta: { actionTime: Date.now(), catalogId },
                payload: response.payload,
                type: LOAD_CATALOG_ANNOUNCMENT_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                meta: { catalogId },
                payload: error,
                type: LOAD_CATALOG_ANNOUNCMENT_FAIL,
            });
        }
    } else {
        return Promise.resolve();
    }
};
